import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useSubscription } from '../context/SubscriptionContext';
import { toast } from 'react-hot-toast';
import { ArrowLeft, Check, CreditCard, QrCode, DollarSign } from 'lucide-react';
import { SubscriptionState } from '../types';
import apiClient from '../services/api';
import { useTranslation } from 'react-i18next';
import { formatPrice } from '../utils/priceFormat';
import subscriptionService from '../services/subscriptionService';
import PaymentErrorDisplay from '../components/ui/PaymentErrorDisplay';
import { formatErrorForDisplay } from '../utils/paymentErrorHandler';
import configService from '../services/configService';
import { FEATURES } from '../config';
import ShutdownNotice from '../components/MaintenanceNotice';

// Define renewal option types
interface RenewalOptionType {
  id: string;
  name: string;
  deliveries: number;
  description: string;
  default: boolean;
  discount: number;
  priceTHB: number;
  priceUSD: number;
  defaultCurrency: string;
}

// Payment method type
type PaymentMethod = 'card' | 'promptpay';
// Currency type
type Currency = 'thb' | 'usd';

export default function RenewalPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { subscriptionId } = useParams<{ subscriptionId: string }>();
  const { subscriptions, isLoading, fetchSubscriptionData } = useSubscription();
  const [selectedOption, setSelectedOption] = useState<string>('quarterly');
  const [isProcessing, setIsProcessing] = useState(false);
  const [subscription, setSubscription] = useState<SubscriptionState | null>(null);
  const [renewalOptions, setRenewalOptions] = useState<RenewalOptionType[]>([]);
  const [isLoadingPrices, setIsLoadingPrices] = useState(true);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod>('card');
  const [selectedCurrency, setSelectedCurrency] = useState<Currency>('thb');
  const [paymentError, setPaymentError] = useState<any>(null);
  const [isRetrying, setIsRetrying] = useState(false);
  const [convertToAutoRenewal, setConvertToAutoRenewal] = useState(false);

  // Fetch renewal options from backend
  useEffect(() => {
    const fetchRenewalOptions = async () => {
      // Wait for subscriptionId to be available
      if (!subscriptionId) {
        return;
      }
      
      try {
        const response = await apiClient.get(`/subscriptions/renewal-options?subscriptionId=${subscriptionId}`);
        
        if (response.data && response.data.data) {
          setRenewalOptions(response.data.data);
          
          // Find and set the default option
          const defaultOption = response.data.data.find((option: RenewalOptionType) => option.default);
          if (defaultOption) {
            setSelectedOption(defaultOption.id);
            setSelectedCurrency(defaultOption.defaultCurrency as Currency || 'thb');
          }
        } else {
          throw new Error('Invalid response format');
        }
        
        setIsLoadingPrices(false);
      } catch (error) {
        console.error('Error fetching renewal options:', error);
        toast.error(t('subscription.renewal.errorMessages.failedToLoadOptions'));
        
        // Get fallback prices from configuration service
        const generateFallbackOptions = async (): Promise<RenewalOptionType[]> => {
          try {
            // Try to get subscription details for correct pricing
            let baseTHB = 999;
            let baseUSD = 29.99;
            
            if (subscription) {
              const serviceType = subscription.serviceType || 'flower';
              const planType = subscription.plan || 'standard';
              
              // Service-specific fallback pricing
              const fallbackPricing = {
                flower: { standard: { thb: 399, usd: 19.99 }, premium: { thb: 599, usd: 24.99 } },
                fruit: { standard: { thb: 999, usd: 32.99 }, premium: { thb: 1299, usd: 42.99 } },
                cat_grass: { standard: { thb: 249, usd: 7.99 }, premium: { thb: 349, usd: 11.99 } }
              };
              
              const servicePricing = fallbackPricing[serviceType as keyof typeof fallbackPricing] || fallbackPricing.flower;
              const planPricing = servicePricing[planType as keyof typeof servicePricing] || servicePricing.standard;
              
              baseTHB = planPricing.thb;
              baseUSD = planPricing.usd;
            } else {
              // Fallback to config service if subscription not available
              const renewalBasePricing = await configService.getRenewalBasePricing();
              baseTHB = renewalBasePricing.standard.thb || 999;
              baseUSD = renewalBasePricing.standard.usd || 29.99;
            }
            
            return [
              { 
                id: 'monthly', 
                name: t('subscription.renewal.options.monthly.name'),
                deliveries: 4, 
                description: t('subscription.renewal.options.monthly.description'),
                default: false, 
                discount: 0, 
                priceTHB: baseTHB,
                priceUSD: baseUSD,
                defaultCurrency: 'thb'
              },
              { 
                id: 'quarterly', 
                name: t('subscription.renewal.options.quarterly.name'),
                deliveries: 12, 
                description: t('subscription.renewal.options.quarterly.description'),
                default: true, 
                discount: 3, 
                priceTHB: baseTHB * 3,
                priceUSD: baseUSD * 3,
                defaultCurrency: 'thb'
              },
              { 
                id: 'biannual', 
                name: t('subscription.renewal.options.biannual.name'),
                deliveries: 26, 
                description: t('subscription.renewal.options.biannual.description'),
                default: false, 
                discount: 5, 
                priceTHB: baseTHB * 6,
                priceUSD: baseUSD * 6,
                defaultCurrency: 'thb'
              },
              { 
                id: 'annual', 
                name: t('subscription.renewal.options.annual.name'),
                deliveries: 52, 
                description: t('subscription.renewal.options.annual.description'),
                default: false, 
                discount: 10, 
                priceTHB: baseTHB * 12,
                priceUSD: baseUSD * 12,
                defaultCurrency: 'thb'
              }
            ];
          } catch (configError) {
            console.error('Error fetching renewal config:', configError);
            // Final hardcoded fallback
            return [
              { 
                id: 'monthly', 
                name: t('subscription.renewal.options.monthly.name'),
                deliveries: 4, 
                description: t('subscription.renewal.options.monthly.description'),
                default: false, 
                discount: 0, 
                priceTHB: 999,
                priceUSD: 29.99,
                defaultCurrency: 'thb'
              },
              { 
                id: 'quarterly', 
                name: t('subscription.renewal.options.quarterly.name'),
                deliveries: 12, 
                description: t('subscription.renewal.options.quarterly.description'),
                default: true, 
                discount: 3, 
                priceTHB: 999 * 3,
                priceUSD: 29.99 * 3,
                defaultCurrency: 'thb'
              },
              { 
                id: 'biannual', 
                name: t('subscription.renewal.options.biannual.name'),
                deliveries: 26, 
                description: t('subscription.renewal.options.biannual.description'),
                default: false, 
                discount: 5, 
                priceTHB: 999 * 6,
                priceUSD: 29.99 * 6,
                defaultCurrency: 'thb'
              },
              { 
                id: 'annual', 
                name: t('subscription.renewal.options.annual.name'),
                deliveries: 52, 
                description: t('subscription.renewal.options.annual.description'),
                default: false, 
                discount: 10, 
                priceTHB: 999 * 12,
                priceUSD: 29.99 * 12,
                defaultCurrency: 'thb'
              }
            ];
          }
        };
        
        const defaultOptions = await generateFallbackOptions();
        
        setRenewalOptions(defaultOptions);
        setIsLoadingPrices(false);
      }
    };
    
    fetchRenewalOptions();
  }, [subscriptionId, subscription]);

  // Fetch subscription data on component mount
  useEffect(() => {
    // Redirect if no subscription ID
    if (!subscriptionId) {
      toast.error(t('subscription.renewal.errorMessages.noSubscriptionSelected'));
      navigate('/subscriptions');
      return;
    }

    const loadSubscription = async () => {
      // Start loading state
      setIsProcessing(true);
      
      try {
        // First check if subscription is already in context
        const foundSubscription = subscriptions.find(s => s.id === subscriptionId);
        
        if (foundSubscription) {
          console.log(`Found subscription in context: ${foundSubscription.id}`);
          setSubscription(foundSubscription);
          setIsProcessing(false);
          return;
        }
        
        // If not found, fetch data
        console.log(`Subscription not in context, fetching: ${subscriptionId}`);
        await fetchSubscriptionData(subscriptionId, true);
        
        // After fetch, give time for context to update and check again
        setTimeout(async () => {
          const fetchedSubscription = subscriptions.find(s => s.id === subscriptionId);
          
          if (fetchedSubscription) {
            console.log(`Found subscription after fetching: ${fetchedSubscription.id}`);
            setSubscription(fetchedSubscription);
            setIsProcessing(false);
            return;
          }
          
          // Final fallback - try direct API call
          try {
            console.log('Attempting direct API call as final fallback');
            const response = await apiClient.get(`/subscriptions/${subscriptionId}`);
            
            if (response.data && response.data.data) {
              console.log('Found subscription via direct API call');
              setSubscription(response.data.data);
              setIsProcessing(false);
              return;
            }
          } catch (directApiError) {
            console.error('Direct API call failed:', directApiError);
          }
          
          console.error('Subscription not found after all attempts');
          toast.error(t('subscription.renewal.errorMessages.unableToFindSubscription'));
          navigate('/subscriptions');
          setIsProcessing(false);
        }, 1000); // Wait 1 second for context to update
      } catch (error) {
        console.error('Error loading subscription:', error);
        toast.error(t('subscription.renewal.errorMessages.failedToLoadDetails'));
        navigate('/subscriptions');
        setIsProcessing(false);
      }
    };

    loadSubscription();
  }, [subscriptionId, navigate, fetchSubscriptionData, subscriptions]);

  // Handle renewal option selection
  const handleOptionSelect = (optionId: string) => {
    setSelectedOption(optionId);
    // Clear any payment errors when user changes selection
    if (paymentError) {
      setPaymentError(null);
    }
    // Auto-renewal is only available for monthly and annual options
    if (optionId !== 'monthly' && optionId !== 'annual' && convertToAutoRenewal) {
      setConvertToAutoRenewal(false);
    }
  };

  // Handle payment method selection
  const handlePaymentMethodSelect = (method: PaymentMethod) => {
    setSelectedPaymentMethod(method);
    // Clear errors when changing payment method
    setPaymentError(null);
    // Disable auto-renewal when PromptPay is selected
    if (method === 'promptpay' && convertToAutoRenewal) {
      setConvertToAutoRenewal(false);
    }
  };

  // Handle currency selection
  const handleCurrencySelect = (currency: Currency) => {
    setSelectedCurrency(currency);
    // If switching to USD, force card payment method
    if (currency === 'usd' && selectedPaymentMethod === 'promptpay') {
      setSelectedPaymentMethod('card');
    }
    // Clear errors when changing currency
    setPaymentError(null);
  };

  // Handle retry payment
  const handleRetryPayment = async () => {
    setIsRetrying(true);
    setPaymentError(null);
    
    try {
      await handleProceedToPayment();
    } finally {
      setIsRetrying(false);
    }
  };

  // Handle contact support
  const handleContactSupport = () => {
    // You can implement this to open a support chat, redirect to support page, etc.
    window.open('mailto:support@piika.co?subject=Payment Issue - Subscription Renewal', '_blank');
  };

  // Handle dismiss error
  const handleDismissError = () => {
    setPaymentError(null);
  };

  // Format currency display
  const formatCurrency = (price: number, currency: Currency) => {
    if (currency === 'thb') {
      return `฿${formatPrice(price, currency)}`;
    } else {
      return `$${formatPrice(price, currency)}`;
    }
  };

  // Handle payment button click
  const handleProceedToPayment = async () => {
    // Check if subscription purchases are enabled
    if (!FEATURES.ENABLE_SUBSCRIPTION_PURCHASES) {
      toast.error(t('common.maintenance.title'));
      return;
    }

    if (!subscriptionId || !subscription) {
      toast.error(t('subscription.renewal.errorMessages.subscriptionInfoMissing'));
      return;
    }

    setIsProcessing(true);

    try {
      // Get the selected option details
      const selectedRenewalOption = renewalOptions.find(option => option.id === selectedOption);
      if (!selectedRenewalOption) {
        throw new Error('Invalid renewal option selected');
      }

      // Get the price based on selected currency
      const basePrice = selectedCurrency === 'thb' 
        ? selectedRenewalOption.priceTHB 
        : selectedRenewalOption.priceUSD;

      // Calculate price with renewal option discount (if any)
      const priceWithRenewalDiscount = selectedRenewalOption.discount > 0
        ? (basePrice * (100 - selectedRenewalOption.discount) / 100)
        : basePrice;

      // Calculate final price with auto-renewal discount (if applicable)
      // Following the same logic as subscription page: 5% discount for auto-renewal
      // But only for monthly and annual options
      const finalPrice = convertToAutoRenewal && selectedPaymentMethod === 'card' && (selectedOption === 'monthly' || selectedOption === 'annual')
        ? priceWithRenewalDiscount * 0.95 // 5% discount for auto-renewal
        : priceWithRenewalDiscount;

      // Create renewal order and payment intent using the renewal API
      const response = await apiClient.post(`/subscriptions/${subscriptionId}/renewal`, {
        renewal_option: selectedOption,
        renewal_deliveries: selectedRenewalOption.deliveries,
        discount_percent: selectedRenewalOption.discount,
        amount: finalPrice,
        currency: selectedCurrency,
        payment_method: selectedPaymentMethod,
        convert_to_recurring: convertToAutoRenewal
      });

      if (!response.data || !response.data.clientSecret) {
        throw new Error('Failed to create payment intent');
      }

      // Get the client secret from the response
      const clientSecret = response.data.clientSecret;
      
      // Store payment info in sessionStorage with correct structure
      const paymentInfo = {
        clientSecret,
        subscriptionId,
        isRenewal: true,
        plan: subscription.plan,
        paymentType: convertToAutoRenewal ? 'subscription' : 'one_time',
        renewalOption: selectedOption,
        deliveries: selectedRenewalOption.deliveries,
        currency: selectedCurrency,
        convertToAutoRenewal: convertToAutoRenewal,
        subscriptionType: selectedOption, // Add subscription type for renewal
        amount: finalPrice // Add amount for price display
      };
      
      sessionStorage.setItem('paymentInfo', JSON.stringify(paymentInfo));
      
      // Redirect to the appropriate payment page based on selected method
      if (selectedPaymentMethod === 'promptpay') {
        navigate(`/qr-payment`, { 
          state: { 
            clientSecret,
            subscriptionId,
            amount: finalPrice,
            paymentType: convertToAutoRenewal ? 'subscription' : 'one_time',
            paymentMethod: 'promptpay',
            isRenewal: true,
            deliveries: selectedRenewalOption.deliveries,
            currency: selectedCurrency,
            convertToAutoRenewal: convertToAutoRenewal,
            subscriptionType: selectedOption // Add subscription type for renewal
          } 
        });
      } else {
        // Default to Stripe/card payment
        navigate(`/stripe-payment`, { 
          state: {
            clientSecret,
            subscriptionId,
            isRenewal: true,
            paymentType: convertToAutoRenewal ? 'subscription' : 'one_time',
            deliveries: selectedRenewalOption.deliveries,
            currency: selectedCurrency,
            plan: subscription.plan,
            convertToAutoRenewal: convertToAutoRenewal,
            subscriptionType: selectedOption, // Add subscription type for renewal
            amount: finalPrice // Add amount for price display
          }
        });
      }
    } catch (error) {
      console.error('Error creating payment intent:', error);
      
      // Clear any previous errors
      setPaymentError(null);
      
      // Format and set the new error
      const errorInfo = formatErrorForDisplay(error);
      setPaymentError(error);
      
      // Show detailed toast message
      toast.error(`${errorInfo.title}: ${errorInfo.message}`);
      
      setIsProcessing(false);
    }
  };

  // Loading state
  if (isLoading || !subscription || isLoadingPrices) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mb-4"></div>
        <p className="text-gray-600">{t('common.loading')}</p>
        {isProcessing && (
          <p className="text-amber-600 mt-4">
            {t('subscription.renewal.errorMessages.fetchingDetails')}
          </p>
        )}
      </div>
    );
  }
  
  // When subscription data is completely invalid
  if (!subscription || !subscription.id) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 flex flex-col items-center justify-center">
        <div className="text-center p-6 bg-white rounded-lg shadow-sm max-w-md">
          <h2 className="text-xl font-semibold mb-4 text-red-600">{t('subscription.renewal.errorMessages.subscriptionNotFound')}</h2>
          <p className="text-gray-600 mb-6">
            {t('subscription.renewal.errorMessages.subscriptionNotFoundDescription')}
          </p>
          <button 
            onClick={() => navigate('/subscriptions')}
            className="bg-amber-500 text-white px-6 py-2 rounded-lg hover:bg-amber-600"
          >
            {t('subscription.renewal.errorMessages.viewMySubscriptions')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white p-4 flex items-center shadow-sm">
        <button onClick={() => navigate(-1)} className="mr-4">
          <ArrowLeft size={24} className="text-gray-700" />
        </button>
        <h1 className="text-xl font-semibold">{t('subscription.renewal.title')}</h1>
      </div>

      {/* Maintenance Notice */}
      {!FEATURES.ENABLE_SUBSCRIPTION_PURCHASES && (
        <div className="m-4">
          <ShutdownNotice />
        </div>
      )}

      {/* Subscription Info */}
      <div className="bg-white m-4 p-4 rounded-lg shadow-sm">
        <h2 className="text-lg font-medium mb-2">{t('subscription.renewal.currentPlan')}</h2>
        <div className="flex justify-between items-center">
          <div>
            <p className="text-gray-700">
              {subscription.plan === 'premium' 
                ? t('subscription.renewal.planLabels.premium') 
                : t('subscription.renewal.planLabels.standard')
              }
            </p>
            {subscription.remainingDeliveries !== undefined && (
              <p className="text-sm text-amber-600">
                {t('subscription.renewal.remainingDeliveries', { count: subscription.remainingDeliveries })}
              </p>
            )}
          </div>
          <div className="bg-amber-100 text-amber-800 text-sm px-3 py-1 rounded-full">
            {subscription.status === 'active' ? t('subscription.details.status.active') : subscription.status}
          </div>
        </div>
      </div>

      {/* Currency Selection */}
      <div className="m-4 mb-2">
        <div className="flex justify-end">
          <div className="inline-flex rounded-md shadow-sm bg-gray-100 p-1" role="group">
            <button
              type="button"
              onClick={() => handleCurrencySelect('thb')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${selectedCurrency === 'thb' ? 'bg-white text-amber-600 shadow-sm' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              THB (฿)
            </button>
            <button
              type="button"
              onClick={() => handleCurrencySelect('usd')}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${selectedCurrency === 'usd' ? 'bg-white text-amber-600 shadow-sm' : 'text-gray-700 hover:bg-gray-50'}`}
            >
              USD ($)
            </button>
          </div>
        </div>
      </div>

      {/* Renewal Options */}
      <div className="m-4">
        <h2 className="text-lg font-medium mb-4">{t('subscription.renewal.selectOption')}</h2>
        
        <div className="space-y-3">
          {renewalOptions.map((option) => {
            // Get price based on selected currency
            const basePrice = selectedCurrency === 'thb' 
              ? option.priceTHB 
              : option.priceUSD;
              
            // Calculate price with renewal option discount (if any)
            const priceWithRenewalDiscount = option.discount > 0 
              ? (basePrice * (100 - option.discount) / 100)
              : basePrice;
              
            // Calculate final price with auto-renewal discount (if applicable)
            // Following the same logic as subscription page: 5% discount for auto-renewal
            // But only for monthly and annual options
            const finalPrice = convertToAutoRenewal && selectedPaymentMethod === 'card' && (option.id === 'monthly' || option.id === 'annual')
              ? priceWithRenewalDiscount * 0.95 // 5% discount for auto-renewal
              : priceWithRenewalDiscount;
              
            // Calculate total discount percentage
            // Only include auto-renewal discount for monthly and annual options
            const autoRenewalDiscount = convertToAutoRenewal && selectedPaymentMethod === 'card' && (option.id === 'monthly' || option.id === 'annual') ? 5 : 0;
            const totalDiscount = option.discount + autoRenewalDiscount;
            
            return (
              <div 
                key={option.id}
                onClick={() => handleOptionSelect(option.id)}
                className={`p-4 rounded-lg border ${selectedOption === option.id ? 'border-amber-500 bg-amber-50' : 'border-gray-200'} cursor-pointer transition-all`}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <div className="flex items-baseline">
                      <h3 className="font-medium">{t(`subscription.renewal.options.${option.id}.name`) || option.name}</h3>
                      <span className="text-xs text-gray-500 ml-2">
                        ({t('subscription.renewal.deliveryCount', { count: option.deliveries })})
                      </span>
                    </div>
                    
                    <div className="mt-2">
                      <span className="text-lg font-semibold text-amber-700">
                        {formatCurrency(finalPrice, selectedCurrency)}
                      </span>
                      {(totalDiscount > 0) && (
                        <span className="text-sm text-gray-500 line-through ml-2">
                          {formatCurrency(basePrice, selectedCurrency)}
                        </span>
                      )}
                    </div>
                    
                    {/* Show discount breakdown */}
                    {totalDiscount > 0 && (
                      <div className="mt-1 space-y-1">
                        <p className="text-sm text-amber-600 font-medium">
                          {t('subscription.renewal.discount', { percent: totalDiscount })}
                        </p>
                        {option.discount > 0 && convertToAutoRenewal && selectedPaymentMethod === 'card' && (option.id === 'monthly' || option.id === 'annual') && (
                          <p className="text-xs text-gray-500">
                            {t('subscription.renewal.discountBreakdown', { 
                              renewalDiscount: option.discount, 
                              autoRenewalDiscount: 5 
                            })}
                          </p>
                        )}
                      </div>
                    )}
                    
                    {/* Show auto-renewal savings - only for monthly and annual options */}
                    {convertToAutoRenewal && selectedPaymentMethod === 'card' && (option.id === 'monthly' || option.id === 'annual') && (
                      <div className="mt-2 bg-green-50 border border-green-200 rounded-md p-2">
                        <p className="text-xs text-green-700">
                          💰 {t('subscription.renewal.autoRenewalSavings', { 
                            amount: formatCurrency(priceWithRenewalDiscount - finalPrice, selectedCurrency) 
                          })}
                        </p>
                      </div>
                    )}
                  </div>
                  {selectedOption === option.id && (
                    <div className="bg-amber-500 text-white p-1 rounded-full">
                      <Check size={16} />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Auto-Renewal availability notice for quarterly and biannual */}
      {(selectedOption === 'quarterly' || selectedOption === 'biannual') && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 m-4">
          <p className="text-sm text-blue-700">
            <strong>{t('subscription.renewal.autoRenewal.notAvailable')}</strong> {t('subscription.renewal.autoRenewal.onlyAvailableForMonthlyAndAnnual')}
          </p>
        </div>
      )}

      {/* Payment Method Selection */}
      <div className="bg-white m-4 p-4 rounded-lg shadow-sm">
        <h2 className="text-lg font-medium mb-3">{t('subscription.renewal.paymentMethods.title')}</h2>
        <div className="grid grid-cols-2 gap-3">
          <div 
            onClick={() => handlePaymentMethodSelect('card')}
            className={`border rounded-lg p-4 flex flex-col items-center justify-center transition ${
              selectedPaymentMethod === 'card' 
                ? 'border-amber-500 bg-amber-50' 
                : 'border-gray-200 hover:border-gray-300'
            } cursor-pointer`}
          >
            <CreditCard size={28} className={`${selectedPaymentMethod === 'card' ? 'text-amber-600' : 'text-gray-600'} mb-2`} />
            <span className="font-medium">{t('subscription.renewal.paymentMethods.creditCard')}</span>
            {selectedPaymentMethod === 'card' && (
              <div className="w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center mt-2">
                <Check size={12} className="text-white" />
              </div>
            )}
          </div>
          
          <div 
            onClick={() => selectedCurrency !== 'usd' && handlePaymentMethodSelect('promptpay')}
            className={`border rounded-lg p-4 flex flex-col items-center justify-center transition ${
              selectedPaymentMethod === 'promptpay' 
                ? 'border-amber-500 bg-amber-50' 
                : 'border-gray-200 hover:border-gray-300'
            } ${selectedCurrency === 'usd' ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <QrCode size={28} className={`${selectedPaymentMethod === 'promptpay' ? 'text-amber-600' : 'text-gray-600'} mb-2`} />
            <span className="font-medium">{t('subscription.renewal.paymentMethods.promptpay')}</span>
            {selectedPaymentMethod === 'promptpay' && (
              <div className="w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center mt-2">
                <Check size={12} className="text-white" />
              </div>
            )}
            {selectedCurrency === 'usd' && (
              <span className="text-xs text-red-500 mt-1">{t('subscription.renewal.paymentMethods.promptpayUnavailable')}</span>
            )}
          </div>
        </div>
      </div>

      {/* Auto-Renewal Option - Only show for monthly and annual options */}
      {(selectedOption === 'monthly' || selectedOption === 'annual') && (
        <div className="bg-white m-4 p-4 rounded-lg shadow-sm">
          <h2 className="text-lg font-medium mb-3">{t('subscription.renewal.autoRenewal.title')}</h2>
          <div className="space-y-3">
            <div 
              onClick={() => selectedPaymentMethod === 'card' && setConvertToAutoRenewal(!convertToAutoRenewal)}
              className={`border rounded-lg p-4 flex items-center justify-between transition ${
                selectedPaymentMethod === 'promptpay' 
                  ? 'cursor-not-allowed opacity-50 border-gray-200' 
                  : 'cursor-pointer ' + (convertToAutoRenewal 
                    ? 'border-amber-500 bg-amber-50' 
                    : 'border-gray-200 hover:border-gray-300'
                  )
              }`}
            >
            <div className="flex-1">
              <div className="flex flex-col space-y-2">
                <div className="flex items-center justify-between">
                  <h3 className={`font-medium ${selectedPaymentMethod === 'promptpay' ? 'text-gray-400' : ''}`}>
                    {t('subscription.renewal.autoRenewal.label')}
                  </h3>
                  {selectedPaymentMethod === 'card' && (
                    <span className="text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                      {t('subscription.renewal.autoRenewal.benefit')}
                    </span>
                  )}
                </div>
                <p className={`text-sm ${selectedPaymentMethod === 'promptpay' ? 'text-gray-400' : 'text-gray-600'}`}>
                  {selectedPaymentMethod === 'promptpay' 
                    ? t('subscription.renewal.autoRenewal.unavailableWithPromptpay')
                    : t('subscription.renewal.autoRenewal.info')
                  }
                </p>
              </div>
            </div>
            <div className="ml-4">
              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                convertToAutoRenewal && selectedPaymentMethod === 'card'
                  ? 'bg-amber-500 border-amber-500' 
                  : 'border-gray-300'
              }`}>
                {convertToAutoRenewal && selectedPaymentMethod === 'card' && (
                  <Check size={12} className="text-white" />
                )}
              </div>
            </div>
          </div>
          
          {selectedPaymentMethod === 'promptpay' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-700">
                <strong>{t('subscription.renewal.autoRenewal.creditCardOnly')}</strong>
              </p>
            </div>
          )}
          
          {convertToAutoRenewal && selectedPaymentMethod === 'card' && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-700">
                <strong>{t('subscription.renewal.autoRenewal.warning')}</strong>
              </p>
            </div>
          )}
        </div>
      </div>
      )}

      {/* Payment Error Display */}
      {paymentError && (
        <div className="m-4">
          <PaymentErrorDisplay
            error={paymentError}
            onRetry={handleRetryPayment}
            onContactSupport={handleContactSupport}
            onDismiss={handleDismissError}
            isRetrying={isRetrying}
          />
        </div>
      )}

      {/* Payment Info */}
      <div className="bg-white m-4 p-4 rounded-lg shadow-sm">
        <h2 className="text-lg font-medium mb-2">{t('subscription.renewal.paymentInfo')}</h2>
        <p className="text-gray-600 text-sm mb-4">
          {t('subscription.renewal.paymentDescription')}
        </p>
      </div>

      {/* Action Button */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-white border-t border-gray-200">
        <button
          onClick={handleProceedToPayment}
          disabled={isProcessing}
          className="w-full py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-lg font-medium flex items-center justify-center"
        >
          {isProcessing ? (
            <span className="flex items-center">
              <span className="animate-spin mr-2 h-4 w-4 border-t-2 border-white rounded-full"></span>
              {t('common.processing')}
            </span>
          ) : (
            <span className="flex items-center">
              {selectedPaymentMethod === 'card' ? (
                <CreditCard size={18} className="mr-2" />
              ) : (
                <QrCode size={18} className="mr-2" />
              )}
              {t('subscription.renewal.proceedToPayment')}
            </span>
          )}
        </button>
      </div>
    </div>
  );
} 